import { Order } from '~/components/EFTable/EFTable.model'
import moment from 'moment'

function tryParseDate(value: any): number | null {
  if (value == null) return null
  const s = String(value).trim()
  if (!s) return null

  const likelyDate = /\d{1,2}\/\d{1,2}\/\d{4}/.test(s)
  if (!likelyDate) return null

  const formats = [
    'D/M/YYYY H:mm:ss',
    'D/M/YYYY H:mm',
    'D/M/YYYY h:mm:ss a',
    'D/M/YYYY h:mm a',
    'DD/MM/YYYY H:mm:ss',
    'DD/MM/YYYY H:mm',
    'DD/MM/YYYY h:mm:ss a',
    'DD/MM/YYYY h:mm a',
    'D/M/YYYY',
    'DD/MM/YYYY'
  ]

  const m = moment(s, formats, true)
  return m.isValid() ? m.valueOf() : null
}

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const rawA = (a[orderBy] as any) ?? ''
  const rawB = (b[orderBy] as any) ?? ''

  // Date comparison
  const dateA = tryParseDate(rawA)
  const dateB = tryParseDate(rawB)
  if (dateA !== null || dateB !== null) {
    const da = dateA ?? -Infinity
    const db = dateB ?? -Infinity
    if (db < da) return -1
    if (db > da) return 1
    return 0
  }

  // Numeric comparison
  const numA = Number(rawA)
  const numB = Number(rawB)
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
    if (numB < numA) return -1
    if (numB > numA) return 1
    return 0
  }

  const sA = rawA?.toString() ?? ''
  const sB = rawB?.toString() ?? ''
  return sB.localeCompare(sA, 'en', { sensitivity: 'base', numeric: true })
}

export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

export function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

/**
 * Takes array of table cell id and handle sticky this cell, add or remove shadow class when scroll to trigger point
 * @param event the scroll event
 * @param cellIds the array of id of table cell need to be sticky
 * @returns void
 */
export function handleHorizontalScrollTable(
  event: React.UIEvent<HTMLDivElement, UIEvent>,
  cellId: string[]
) {
  const container = event.currentTarget
  const style = window.getComputedStyle(container)
  const horizontalDiff = container.scrollWidth - container.clientWidth
  const hasHorizontalScroll = style.overflowX !== 'hidden' && horizontalDiff > 8

  const getCols = (name: string) =>
    Array.from(document.getElementsByClassName(`${name}-col`)) as HTMLElement[]

  const clearAll = () => {
    cellId.forEach(name => {
      getCols(name).forEach(c => {
        c.classList.remove('scroll-shadow')
        c.style.left = ''
      })
    })
  }

  const current = Math.max(0, Math.floor(container.scrollLeft || 0))
  const last = Number(container.dataset.lastScrollLeft ?? 0)

  if (!hasHorizontalScroll) {
    clearAll()
    container.dataset.lastScrollLeft = '0'
    return
  }

  // ignore vertical-only scrolls
  if (current === last) return

  // if scrolled back to start, clear and store state
  if (current === 0) {
    clearAll()
    container.dataset.lastScrollLeft = '0'
    return
  }

  container.dataset.lastScrollLeft = String(current)

  for (let i = 0; i < cellId.length; i++) {
    const name = cellId[i]
    const cols = getCols(name)
    if (!cols.length) continue

    // first sticky column: show shadow when scrolled right
    if (i === 0) {
      if (current > 0) {
        cols.forEach(c => {
          c.classList.add('scroll-shadow')
          c.style.left = '0px'
        })
      } else {
        cols.forEach(c => {
          c.classList.remove('scroll-shadow')
          c.style.left = ''
        })
      }
      continue
    }

    const prevLists = cellId.slice(0, i).map(id => getCols(id))
    if (prevLists.some(list => !list.length)) continue

    const totalPrevWidth = prevLists.reduce(
      (sum, list) => sum + (list[0]?.clientWidth ?? 0),
      0
    )

    const containerRect = container.getBoundingClientRect()
    const colRect = cols[0].getBoundingClientRect()
    const colLeftRelative = colRect.left - containerRect.left

    if (colLeftRelative <= totalPrevWidth + 1) {
      cols.forEach(c => {
        c.classList.add('scroll-shadow')
        c.style.left = `${totalPrevWidth - 1}px`
      })
    } else {
      cols.forEach(c => {
        c.classList.remove('scroll-shadow')
        c.style.left = ''
      })
    }
  }
}
